// BookVehicle rebuild — strict Vauren design system, 3-step flow.
// Strict dependency whitelist; no GSAP, no helmet, no 3D.
import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/PageLayout";
import { SEOHead } from "@/components/SEOHead";
import { toast } from "sonner";
import { format, differenceInDays, parseISO, eachDayOfInterval, addDays } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  Plus,
  Check,
  ChevronLeft,
  Shield,
  MapPin,
  Truck,
  Users as UsersIcon,
  Package,
  Wifi,
  Baby,
  Sparkles,
  KeyRound,
  Fuel,
  Snowflake,
  Music,
  Navigation,
  Camera,
  Briefcase,
  Mountain,
  Tent,
  Dog,
  Coffee,
  Droplet,
  Bed,
  Tv,
  Bike,
  Trash2,
  ChevronDown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { getEffectiveLocation } from "@/lib/vehicle-location";
import { describePolicy } from "@/lib/cancellationPolicy";
import BookingEligibilityCheck, {
  checkBookingEligibility,
} from "@/components/marketplace/BookingEligibilityCheck";
import { track } from "@/lib/analytics";
import { readBookingIntent } from "@/lib/booking-intent";
import AddressAutocomplete from "@/components/marketplace/AddressAutocomplete";
import {
  VAUREN_SERVICE_FEE_RATE,
  getServiceFee,
  HOST_REVENUE_SHARE,
  getSecurityDeposit,
  getMultiDayDiscount,
} from "@/lib/rental-constants";

// ─── Design tokens ───────────────────────────────────────────────────
const C = {
  page: "#F5F0EB",
  surface: "#FFFFFF",
  text: "#2C2420",
  textMuted: "#4F413E",
  taupe: "#9C9088",
  border: "#E0D8D2",
  borderSoft: "#F0EBE6",
  bronze: "#997E54",
  bronzeDark: "#997E54",
  error: "#DC3545",
} as const;

const SERIF = '"Cormorant Garamond", serif';
const SANS = '"DM Sans", system-ui, sans-serif';

const OCCASION_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Select occasion (optional)" },
  { value: "anniversary", label: "Anniversary or romantic trip" },
  { value: "corporate", label: "Corporate event or retreat" },
  { value: "family", label: "Family road trip" },
  { value: "weekend", label: "Weekend adventure" },
  { value: "airport", label: "Airport transfer" },
  { value: "moving", label: "Moving day" },
  { value: "other", label: "Other" },
];

// Responsive layout — inline media query so the right rail moves above
// the form on mobile per the design system.
const RESPONSIVE_CSS = `
.bv-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: start;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}
@media (min-width: 960px) {
  .bv-grid {
    grid-template-columns: minmax(0, 1fr) 380px;
    gap: 48px;
  }
}
@media (max-width: 959px) {
  /* Push the booking summary BELOW the main step content on tablet
     and mobile — see also the accordion toggle below, which keeps the
     summary collapsed by default on narrow viewports. */
  .bv-grid > .bv-summary {
    order: 2;
  }
  .bv-grid > main {
    order: 1;
  }
  /* The collapse / expand bar — visible only on narrow viewports. */
  .bv-summary-mobile-toggle {
    display: flex !important;
  }
}
@media (max-width: 768px) {
  .bv-grid {
    padding-left: 16px !important;
    padding-right: 16px !important;
    gap: 16px !important;
  }
  .bv-grid > .bv-summary {
    position: relative !important;
    top: auto !important;
  }
  .bv-card { padding: 16px !important; }
  .bv-hero { height: 160px !important; margin-bottom: 16px !important; }
  .bv-hero-content { padding: 18px 20px !important; }
  .bv-hero-title { font-size: 22px !important; }
  /* Keep step labels visible on mobile — only shrink and tighten so
     they fit. The connector lines hide on mobile to make room. */
  .bv-step-label {
    font-size: 11px !important;
    letter-spacing: 0.01em !important;
  }
  .bv-step-connector { display: none !important; }
  .bv-step-progress {
    padding: 12px 0 16px !important;
    gap: 4px !important;
    max-width: 100% !important;
  }
  .bv-topheader {
    padding: 12px 16px !important;
    gap: 8px !important;
  }
  .bv-back-text { display: none !important; }
  .bv-topheader-wordmark {
    font-size: 14px !important;
    letter-spacing: 0.12em !important;
  }
  .bv-topheader-title {
    font-size: 11px !important;
    max-width: 45% !important;
  }
}
@media (max-width: 380px) {
  .bv-topheader-title { display: none !important; }
}
/* Mobile booking-summary accordion toggle — hidden on desktop. */
.bv-summary-mobile-toggle { display: none; }
/* 48px minimum tap target for every primary button on this page. */
.bv-cta { min-height: 48px; }
.bv-input::placeholder { color: ${C.taupe}; }
.bv-input:focus {
  outline: none;
  border-color: ${C.bronze};
  box-shadow: 0 0 0 3px rgba(153,126,84,0.15);
}
.bv-textarea::placeholder { color: ${C.taupe}; }
.bv-textarea:focus {
  outline: none;
  border-color: ${C.bronze};
  box-shadow: 0 0 0 3px rgba(153,126,84,0.15);
}
.bv-select:focus {
  outline: none;
  border-color: ${C.bronze};
  box-shadow: 0 0 0 3px rgba(153,126,84,0.15);
}
.bv-cta:hover:not(:disabled) {
  background: ${C.bronzeDark} !important;
  transform: translateY(-1px);
}
.bv-link:hover { color: ${C.bronze} !important; }
`;

// ─── Reusable primitives ─────────────────────────────────────────────
const Label = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontFamily: SANS,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: C.taupe,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2
    style={{
      fontFamily: SERIF,
      fontSize: 22,
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: C.text,
      margin: 0,
      marginBottom: 16,
    }}
  >
    {children}
  </h2>
);

const Divider = () => (
  <div style={{ height: 1, background: C.border, margin: "24px 0" }} />
);

const Card = ({ children, padding = 24 }: { children: React.ReactNode; padding?: number }) => (
  <div
    className="bv-card"
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding,
      boxShadow:
        "0 1px 3px rgba(44,36,32,0.04), 0 4px 16px rgba(44,36,32,0.06)",
    }}
  >
    {children}
  </div>
);

const PrimaryButton = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    className="bv-cta"
    onClick={onClick}
    disabled={disabled}
    style={{
      width: "100%",
      padding: "14px 24px",
      background: disabled ? C.border : C.bronze,
      color: "#FFFFFF",
      border: "none",
      borderRadius: 8,
      fontFamily: SERIF,
      fontSize: 16,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      boxShadow: disabled
        ? "none"
        : "0 1px 2px rgba(44,36,32,0.06), 0 2px 8px rgba(44,36,32,0.04)",
    }}
  >
    {children}
  </button>
);

const TextLink = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="bv-link"
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      padding: 0,
      fontFamily: SANS,
      fontSize: 14,
      fontWeight: 500,
      color: C.text,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      transition: "color 0.2s ease",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    }}
  >
    {children}
  </button>
);

// ─── Step progress indicator ─────────────────────────────────────────
type StepKey = 1 | 2 | 3 | 4;

const STEPS: { key: StepKey; label: string }[] = [
  { key: 1, label: "Review" },
  { key: 2, label: "Add-ons" },
  { key: 3, label: "Pay" },
  { key: 4, label: "Confirm" },
];

interface AddonOption {
  id: string;
  icon: string;
  name: string;
  desc: string;
  price: number;
  unit: string;
  perDay?: boolean;
}

// Row from public.vehicle_extras for the current vehicle. is_free
// extras default to selected on first load; max_quantity is read
// but the UI only supports quantity 1 in this pass (matches the
// SprinterRentals layout the spec referenced).
interface VehicleExtra {
  id: string;
  name: string;
  description: string | null;
  price: number;
  price_type: "per_booking" | "per_day" | string;
  max_quantity: number;
  is_active: boolean;
  is_free: boolean;
  icon: string | null;
  sort_order: number;
}

// Icon-key → lucide component map. Hosts pick an icon key when they
// build the extra; anything not in this map falls back to Package
// so the row always renders an icon. Keys are lowercased lookups so
// "WiFi" / "wifi" / "WIFI" all resolve.
const EXTRA_ICONS: Record<string, LucideIcon> = {
  package: Package,
  wifi: Wifi,
  hotspot: Wifi,
  shield: Shield,
  protection: Shield,
  insurance: Shield,
  baby: Baby,
  child_seat: Baby,
  car_seat: Baby,
  sparkles: Sparkles,
  detailing: Sparkles,
  cleaning: Sparkles,
  key: KeyRound,
  keys: KeyRound,
  fuel: Fuel,
  gas: Fuel,
  snowflake: Snowflake,
  cooler: Snowflake,
  ice: Snowflake,
  music: Music,
  speaker: Music,
  audio: Music,
  navigation: Navigation,
  gps: Navigation,
  camera: Camera,
  dashcam: Camera,
  briefcase: Briefcase,
  business: Briefcase,
  mountain: Mountain,
  outdoor: Mountain,
  tent: Tent,
  camping: Tent,
  dog: Dog,
  pet: Dog,
  coffee: Coffee,
  droplet: Droplet,
  water: Droplet,
  bed: Bed,
  bedding: Bed,
  tv: Tv,
  entertainment: Tv,
  bike: Bike,
  bicycle: Bike,
  map: MapPin,
  truck: Truck,
  users: UsersIcon,
};

const resolveExtraIcon = (key: string | null | undefined): LucideIcon => {
  if (!key) return Package;
  return EXTRA_ICONS[key.toLowerCase().trim()] || Package;
};

const extraUnitLabel = (priceType: string): string => {
  if (priceType === "per_day") return "per day";
  return "per booking";
};

const formatMoney = (n: number) =>
  n === 0
    ? "$0.00"
    : `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const ADDONS: AddonOption[] = [
  {
    id: "detailing",
    icon: "✨",
    name: "Premium return detailing",
    desc: "Return the van without the stress of cleaning. We handle it.",
    price: 50,
    unit: "one-time",
  },
  {
    id: "roadside",
    icon: "🛡️",
    name: "Roadside assistance",
    desc: "Peace of mind for the whole trip. Flat tyre, battery, lockout.",
    price: 25,
    unit: "per trip",
  },
  {
    id: "child_seat",
    icon: "👶",
    name: "Child safety seat",
    desc: "Professionally fitted, age-appropriate, sanitised between trips.",
    price: 15,
    unit: "per day",
    perDay: true,
  },
  {
    id: "wifi",
    icon: "📶",
    name: "Mobile WiFi hotspot",
    desc: "Stay connected anywhere. Unlimited data, no throttling.",
    price: 20,
    unit: "per day",
    perDay: true,
  },
];

const StepProgress = ({
  current,
  steps = STEPS,
}: {
  current: StepKey;
  steps?: typeof STEPS;
}) => (
  <div
    className="bv-step-progress"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: "24px 0 32px",
      maxWidth: 480,
      margin: "0 auto",
    }}
  >
    {steps.map((s, i) => {
      const completed = s.key < current;
      const active = s.key === current;
      const numColor = completed ? C.bronze : active ? C.text : C.taupe;
      const labelColor = completed ? C.bronze : active ? C.text : C.taupe;
      const fontWeight = active ? 600 : 400;
      return (
        <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: `1px solid ${completed ? C.bronze : active ? C.text : C.border}`,
                background: completed ? C.bronze : "transparent",
                color: completed ? "#FFFFFF" : numColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: SANS,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {completed ? <Check size={13} /> : s.key}
            </div>
            <span
              className="bv-step-label"
              style={{
                fontFamily: SANS,
                fontSize: 13,
                fontWeight,
                color: labelColor,
                letterSpacing: "0.02em",
              }}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className="bv-step-connector"
              style={{ flex: 1, height: 1, background: C.border }}
            />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Price summary card ──────────────────────────────────────────────
interface PriceLine {
  label: string;
  value: string;
  muted?: boolean;
}

const PriceRow = ({ label, value, muted }: PriceLine) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      padding: "10px 0",
      borderBottom: `1px solid ${C.borderSoft}`,
      fontFamily: SANS,
      fontSize: 14,
      color: muted ? C.textMuted : C.text,
    }}
  >
    <span>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

interface PriceSummaryProps {
  vehicleTitle: string;
  vehiclePhoto: string;
  pickupCity: string;
  dateRangeText: string;
  totalDays: number;
  dailyPrice: number;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  cleaningFee: number;
  deliveryFee: number;
  platformFee: number;
  totalPrice: number;
  securityDeposit: number;
  // Extras rendered as individual PriceRows so the renter can see
  // exactly which add-ons contribute to the total. Free extras render
  // a "$0.00" row so they're visible but don't move the bottom line.
  extras?: { id: string; name: string; total: number; is_free: boolean }[];
}

const PriceSummary = (p: PriceSummaryProps) => (
  <Card>
    <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
      <div
        style={{
          width: 76,
          height: 76,
          borderRadius: 8,
          overflow: "hidden",
          flexShrink: 0,
          background: C.borderSoft,
        }}
      >
        <img
          src={p.vehiclePhoto}
          alt={p.vehicleTitle}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontFamily: SERIF,
            fontSize: 17,
            fontWeight: 600,
            color: C.text,
            letterSpacing: "-0.005em",
            lineHeight: 1.25,
            marginBottom: 4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {p.vehicleTitle}
        </div>
        <div
          style={{
            fontFamily: SANS,
            fontSize: 13,
            color: C.taupe,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <MapPin size={12} color={C.bronze} />
          {p.pickupCity}
        </div>
      </div>
    </div>

    <div
      style={{
        background: C.page,
        borderRadius: 8,
        padding: "10px 12px",
        marginBottom: 16,
        fontFamily: SANS,
        fontSize: 13,
        color: C.text,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: C.taupe, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
        Trip dates
      </div>
      <div style={{ fontWeight: 500 }}>{p.dateRangeText}</div>
      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
        {p.totalDays} {p.totalDays === 1 ? "night" : "nights"}
      </div>
    </div>

    <PriceRow
      label={`$${p.dailyPrice.toLocaleString()} × ${p.totalDays} ${p.totalDays === 1 ? "night" : "nights"}`}
      value={`$${p.subtotal.toLocaleString()}`}
    />
    {p.discountAmount > 0 && (
      <PriceRow
        label={`${p.discountPercent}% multi-day discount`}
        value={`-$${p.discountAmount.toLocaleString()}`}
      />
    )}
    {p.cleaningFee > 0 && (
      <PriceRow label="Cleaning fee" value={`$${p.cleaningFee.toLocaleString()}`} />
    )}
    {p.deliveryFee > 0 && (
      <PriceRow label="Delivery" value={`$${p.deliveryFee.toLocaleString()}`} />
    )}
    {p.extras && p.extras.length > 0 && (
      <>
        {p.extras.map((ex) => (
          <PriceRow
            key={ex.id}
            label={ex.name}
            value={
              ex.is_free
                ? "$0.00"
                : `$${ex.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            }
            muted={ex.is_free}
          />
        ))}
      </>
    )}
    <PriceRow
      label={`Vauren service fee (${Math.round(VAUREN_SERVICE_FEE_RATE * 100)}%)`}
      value={`$${p.platformFee.toLocaleString()}`}
    />
    <PriceRow
      label="Security deposit"
      value={`$${p.securityDeposit.toLocaleString()}`}
    />
    <div
      style={{
        fontFamily: SANS,
        fontSize: 12,
        color: "rgba(44, 36, 32, 0.55)",
        lineHeight: 1.5,
        padding: "8px 0 12px",
      }}
    >
      Collected at checkout. Refunded automatically within 5 business days of
      trip completion if no damage is reported. You will not be charged twice.
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        paddingTop: 16,
        marginTop: 8,
        borderTop: `2px solid ${C.border}`,
        fontFamily: SERIF,
        color: C.text,
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 600 }}>Total</span>
      <span style={{ fontSize: 24, fontWeight: 600 }}>
        ${(p.totalPrice + p.securityDeposit).toLocaleString()}
      </span>
    </div>

    <div
      style={{
        marginTop: 16,
        padding: 12,
        background: C.page,
        borderRadius: 8,
        fontFamily: SANS,
        fontSize: 12,
        color: C.textMuted,
        lineHeight: 1.5,
      }}
    >
      The $<strong style={{ color: C.text }}>{p.securityDeposit.toLocaleString()}</strong> security deposit is
      refunded within 5 business days of a clean return.
    </div>
  </Card>
);

// ─── Top header ──────────────────────────────────────────────────────
const TopHeader = ({ vehicleTitle, onBack }: { vehicleTitle: string; onBack: () => void }) => (
  <div
    className="bv-topheader"
    style={{
      background: C.surface,
      borderBottom: `1px solid ${C.border}`,
      padding: "16px 24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      minWidth: 0,
    }}
  >
    <button
      type="button"
      onClick={onBack}
      className="bv-link bv-back-btn"
      aria-label="Back to listing"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: SANS,
        fontSize: 13,
        fontWeight: 500,
        color: C.text,
        cursor: "pointer",
        transition: "color 0.2s ease",
        flexShrink: 0,
      }}
    >
      <ChevronLeft size={16} />
      <span className="bv-back-text">Back to listing</span>
    </button>
    <span
      className="bv-topheader-wordmark"
      style={{
        fontFamily: SERIF,
        fontSize: 17,
        fontWeight: 600,
        letterSpacing: "0.18em",
        color: C.text,
        flexShrink: 0,
      }}
    >
      VAUREN
    </span>
    <span
      className="bv-topheader-title"
      style={{
        fontFamily: SANS,
        fontSize: 12,
        color: C.taupe,
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textAlign: "right",
        minWidth: 0,
      }}
    >
      {vehicleTitle}
    </span>
  </div>
);

// ─── sessionStorage state persistence ───────────────────────────────
// When the renter clicks "Pay" we drop them on Stripe Checkout. If they
// hit the browser back button (or Stripe's own cancel link), we need to
// land them back on this page with every form field intact — not on the
// listing with everything reset. Keyed per-vehicle so multiple tabs
// don't clobber each other.
const stateKey = (vehicleId: string | undefined) =>
  vehicleId ? `vauren_book_state_${vehicleId}` : "";

interface PersistedState {
  selectedAddons: string[];
  selectedExtraIds?: string[];
  pickupType: "pickup" | "delivery";
  deliveryAddress: string;
  notes: string;
  passengerCount: number;
  occasion: string;
  pickupTime: string;
  dropoffTime: string;
  step: 1 | 2 | 3 | 4;
  startDate: string;
  endDate: string;
  savedAt: string;
}

const readPersistedState = (vehicleId: string | undefined): PersistedState | null => {
  if (!vehicleId || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(stateKey(vehicleId));
    if (!raw) return null;
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
};

const writePersistedState = (vehicleId: string | undefined, state: PersistedState): void => {
  if (!vehicleId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(stateKey(vehicleId), JSON.stringify(state));
  } catch {
    // Quota / private-browsing — non-blocking.
  }
};

export const clearPersistedBookingState = (vehicleId: string | undefined): void => {
  if (!vehicleId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(stateKey(vehicleId));
  } catch {
    // ignore
  }
};

// ─── Main component ─────────────────────────────────────────────────
const BookVehicle = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Dates can come from (in order of priority):
  //   1. URL params (?start / ?from / ?end / ?to) — deep-link wins.
  //   2. Per-vehicle persisted BookVehicle form state — recovery
  //      after a Stripe cancel mid-flow.
  //   3. Cross-page booking intent saved on the VehicleDetail
  //      Reserve click — restores dates after the auth + email-
  //      verification round-trip strips the original URL params.
  //      We only honour intent whose vehicleId matches this page;
  //      otherwise an old intent from a different van could
  //      pre-fill garbage dates here.
  const persistedOnMount = readPersistedState(id);
  const intentOnMount = readBookingIntent();
  const matchedIntent =
    intentOnMount && id && intentOnMount.vehicleId === id ? intentOnMount : null;
  const startDate =
    searchParams.get("start") || searchParams.get("from") ||
    persistedOnMount?.startDate ||
    matchedIntent?.startDate ||
    "";
  const endDate =
    searchParams.get("end") || searchParams.get("to") ||
    persistedOnMount?.endDate ||
    matchedIntent?.endDate ||
    "";

  // Render the cancelled-checkout banner when Stripe sends the renter back
  // via the cancel_url we provide. Stays visible until the renter takes any
  // further action (we don't bother dismissing it explicitly).
  const cameFromCancelledCheckout = searchParams.get("cancelled") === "true";

  const [vehicle, setVehicle] = useState<any>(null);
  const [owner, setOwner] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [renterProfile, setRenterProfile] = useState<any>(null);
  const [vehiclePhoto, setVehiclePhoto] = useState<string>("/placeholder.svg");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<StepKey>(1);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  // Mobile-only accordion state for the booking summary. Defaults open
  // on >=960px so the summary stays visible alongside the form, and
  // closed on narrow viewports so renters aren't scrolled past the
  // step content. matchMedia is checked once on mount; viewport
  // resizes between breakpoints during the same session are rare
  // enough not to warrant a listener.
  const [summaryOpen, setSummaryOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 960px)").matches;
  });
  // Extras come from vehicle_extras. Loaded once per vehicle. Free
  // extras are auto-selected the first time the list resolves so the
  // renter doesn't have to tap zero-cost items individually; subsequent
  // updates leave the renter's choices intact.
  const [extras, setExtras] = useState<VehicleExtra[]>([]);
  const [extrasLoaded, setExtrasLoaded] = useState(false);
  // skipExtras is true once we know the catalogue is empty for this
  // vehicle. The Review CTA jumps straight to Pay, the Pay back-link
  // jumps back to Review, and StepProgress hides the Add-ons step.
  const skipExtras = extrasLoaded && extras.length === 0;
  const [selectedExtraIds, setSelectedExtraIds] = useState<string[]>([]);

  // Form state
  const [pickupType, setPickupType] = useState<"pickup" | "delivery">("pickup");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Flips to true on the first Reserve click. Gates the visibility of
  // the inline red error messages below each required field so the
  // form stays clean on first load and only shows errors after the
  // user has actually tried to submit.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [passengerCount, setPassengerCount] = useState(6);
  const [occasion, setOccasion] = useState("");
  // Pickup / drop-off times of day. Stored as HH:mm and combined with
  // the date columns into timestamptz when we submit the booking.
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropoffTime, setDropoffTime] = useState("10:00");

  // Restore persisted form state on mount. Only runs once per vehicle id.
  // We bail out if the persisted start date is in the past — stale state
  // from a months-old session shouldn't override fresh URL params.
  useEffect(() => {
    if (!id) return;
    const persisted = readPersistedState(id);
    if (!persisted) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const persistedStart = persisted.startDate ? new Date(persisted.startDate + "T23:59:59") : null;
    if (!persistedStart || persistedStart.getTime() < todayStart.getTime()) {
      clearPersistedBookingState(id);
      return;
    }
    setSelectedAddons(persisted.selectedAddons ?? []);
    // Restore vehicle_extras selection so a renter who bailed at
    // Stripe and came back doesn't have to re-pick everything.
    if (Array.isArray(persisted.selectedExtraIds)) {
      setSelectedExtraIds(persisted.selectedExtraIds);
    }
    setPickupType(persisted.pickupType ?? "pickup");
    setDeliveryAddress(persisted.deliveryAddress ?? "");
    setNotes(persisted.notes ?? "");
    setPassengerCount(persisted.passengerCount ?? 6);
    setOccasion(persisted.occasion ?? "");
    setPickupTime(persisted.pickupTime ?? "10:00");
    setDropoffTime(persisted.dropoffTime ?? "10:00");
    // Step is restored too so the renter lands back where they left off.
    // We don't restore agreedToTerms — legal step, must be re-confirmed.
    if (persisted.step && persisted.step >= 1 && persisted.step <= 4) {
      setStep(persisted.step as StepKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Force pickupType to "delivery" when the host offers delivery only
  // (no choice for the renter). Runs whenever the vehicle loads so a
  // persisted "pickup" choice from a different listing can't leak in.
  useEffect(() => {
    if (!vehicle) return;
    const opt =
      vehicle.delivery_option ||
      (vehicle.delivery_available
        ? vehicle.pickup_type === "both"
          ? "both"
          : "delivery_available"
        : "pickup_only");
    if (opt === "delivery_available") {
      setPickupType("delivery");
    } else if (opt === "pickup_only" && pickupType === "delivery") {
      setPickupType("pickup");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id]);

  useEffect(() => {
    const load = async () => {
      const {
        data: { user: u },
      } = await supabase.auth.getUser();
      if (!u) {
        const bookingUrl = `/marketplace/book/${id}?start=${startDate}&end=${endDate}`;
        navigate(`/marketplace/auth?redirect=${encodeURIComponent(bookingUrl)}`);
        return;
      }
      setUser(u);

      const { ensureMarketplaceAccount } = await import("@/lib/marketplace-auth");
      await ensureMarketplaceAccount(u);

      const [vRes, rpRes] = await Promise.all([
        supabase.from("vehicles").select("*").eq("id", id).single(),
        supabase.from("renter_profiles").select("*").eq("user_id", u.id).maybeSingle(),
      ]);

      if (!vRes.data) {
        toast.error("Vehicle not found");
        navigate("/rent-a-van");
        return;
      }
      if (!vRes.data.is_approved || !vRes.data.is_active) {
        toast.error("This vehicle is currently unavailable.");
        navigate("/rent-a-van");
        return;
      }
      setVehicle(vRes.data);
      if (rpRes.data) setRenterProfile(rpRes.data);

      const [oRes, photoRes] = await Promise.all([
        supabase.rpc("get_owner_public_profile", { _user_id: vRes.data.owner_id }),
        supabase
          .from("vehicle_photos")
          .select("photo_url")
          .eq("vehicle_id", id!)
          .eq("is_primary", true)
          .limit(1),
      ]);
      if (oRes.data && oRes.data.length > 0) setOwner(oRes.data[0]);
      if (photoRes.data && photoRes.data.length > 0) {
        setVehiclePhoto(photoRes.data[0].photo_url);
      } else {
        const fallback = await supabase
          .from("vehicle_photos")
          .select("photo_url")
          .eq("vehicle_id", id!)
          .order("sort_order")
          .limit(1);
        if (fallback.data && fallback.data.length > 0) setVehiclePhoto(fallback.data[0].photo_url);
      }

      setLoading(false);
    };
    load();
  }, [id, navigate, startDate, endDate]);

  // Auto-skip step 2 if the host hasn't configured any add-ons.
  // Runs after extras finish loading; the brief flash on the empty
  // state is avoided because we don't render step 2 at all once we
  // know it's empty.
  useEffect(() => {
    if (step === 2 && skipExtras) {
      setStep(3);
    }
  }, [step, skipExtras]);

  // Load active vehicle_extras once per vehicle. is_free rows are
  // auto-selected on first resolution; we gate the auto-select with
  // extrasLoaded so a renter's manual deselection isn't reverted on
  // a re-fetch.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("vehicle_extras")
        .select(
          "id, name, description, price, price_type, max_quantity, is_active, is_free, icon, sort_order",
        )
        .eq("vehicle_id", id)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (cancelled) return;
      if (error) {
        console.warn("[BookVehicle] vehicle_extras load failed", error);
        setExtras([]);
        setExtrasLoaded(true);
        return;
      }
      const rows = (data || []) as VehicleExtra[];
      setExtras(rows);
      setSelectedExtraIds((prev) => {
        // Preserve any prior selection (from sessionStorage restore); only
        // auto-add free extras on the very first load.
        if (extrasLoaded) return prev;
        const freeIds = rows.filter((r) => r.is_free).map((r) => r.id);
        const merged = Array.from(new Set([...prev, ...freeIds]));
        return merged;
      });
      setExtrasLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!vehicle || !startDate || !endDate) return;
    const days = differenceInDays(parseISO(endDate), parseISO(startDate));
    track.renter.bookingPageViewed({
      vehicleId: vehicle.id,
      title: vehicle.title,
      totalDays: days,
      totalPrice: days * vehicle.daily_price,
    });
  }, [vehicle, startDate, endDate]);

  // ─── Loading state ───
  if (loading) {
    return (
      <PageLayout>
        <div
          style={{
            minHeight: "100vh",
            background: C.page,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader2 size={32} color={C.bronze} className="animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!vehicle || !startDate || !endDate) {
    return (
      <PageLayout>
        <div
          style={{
            minHeight: "100vh",
            background: C.page,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 24,
          }}
        >
          <div style={{ fontFamily: SERIF, fontSize: 24, color: C.text, fontWeight: 600 }}>
            Booking details missing
          </div>
          <p style={{ fontFamily: SANS, fontSize: 14, color: C.textMuted, textAlign: "center", maxWidth: 360 }}>
            We couldn't load the trip dates for this booking. Return to the listing and pick your dates again.
          </p>
          <button
            onClick={() => navigate(`/marketplace/vehicle/${id}`)}
            style={{
              padding: "12px 20px",
              background: C.bronze,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 8,
              fontFamily: SERIF,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Back to listing
          </button>
        </div>
      </PageLayout>
    );
  }

  // ─── Pricing ───
  const totalDays = differenceInDays(parseISO(endDate), parseISO(startDate));
  const subtotal = totalDays * vehicle.daily_price;
  const discountFraction = getMultiDayDiscount(totalDays);
  const discountPercent = Math.round(discountFraction * 100);
  const discountAmount = Math.round(subtotal * discountFraction * 100) / 100;
  const cleaningFee = vehicle.cleaning_fee;
  const rawDeliveryFee = vehicle.delivery_fee || 0;
  const freeThreshold = vehicle.free_delivery_threshold || 0;
  const deliveryFeeWaived =
    pickupType === "delivery" && freeThreshold > 0 && subtotal >= freeThreshold;
  const deliveryFee =
    pickupType === "delivery" ? (deliveryFeeWaived ? 0 : rawDeliveryFee) : 0;
  // Renter service fee: 8% of rental subtotal (nightly × nights) before
  // any add-ons or other fees. Computed off the post-discount subtotal
  // would let multi-day discounts erode the fee — keep it on raw subtotal
  // so the rate the renter sees matches the rate Vauren publishes.
  const platformFee = getServiceFee(subtotal);
  // Per-extra computed line totals, derived from vehicle_extras +
  // selectedExtraIds. is_free always contributes 0 regardless of the
  // price column (lets a host record a non-zero "reference value" on
  // a complimentary extra without it costing the renter).
  const selectedExtraRows = extras.filter((e) => selectedExtraIds.includes(e.id));
  const extrasLineItems = selectedExtraRows.map((e) => {
    const perDay = e.price_type === "per_day";
    const quantity = perDay ? Math.max(1, totalDays) : 1;
    const unitPrice = e.is_free ? 0 : Number(e.price) || 0;
    const lineTotal = Math.round(unitPrice * quantity * 100) / 100;
    return {
      id: e.id,
      name: e.name,
      price: unitPrice,
      price_type: e.price_type,
      quantity,
      total: lineTotal,
      is_free: e.is_free,
    };
  });
  const addonTotal = extrasLineItems.reduce((sum, l) => sum + l.total, 0);
  const totalPrice =
    subtotal - discountAmount + cleaningFee + deliveryFee + platformFee + addonTotal;
  const hostEarnings = subtotal - discountAmount + cleaningFee + deliveryFee;
  const hostPayout = Math.round(hostEarnings * HOST_REVENUE_SHARE * 100) / 100;
  const securityDeposit = getSecurityDeposit(vehicle.vehicle_type || "sprinter_van");

  // Delivery availability — prefer the new delivery_option enum
  // ('pickup_only' | 'delivery_available' | 'both'). Falls back to
  // the legacy delivery_available + pickup_type combo for listings
  // that predate the migration.
  const deliveryOpt: "pickup_only" | "delivery_available" | "both" =
    (vehicle as any).delivery_option ||
    (vehicle.delivery_available
      ? vehicle.pickup_type === "both"
        ? "both"
        : "delivery_available"
      : "pickup_only");
  // The renter can pick between pickup and delivery only when both
  // are offered. When only delivery is offered, pickupType is forced
  // to "delivery" so the fee + address fields appear automatically.
  const showPickupDeliveryChoice = deliveryOpt === "both";
  const deliveryAvailable = deliveryOpt !== "pickup_only";
  const maxPassengers = vehicle.passenger_capacity || 12;
  const dateRangeText = `${format(parseISO(startDate), "MMM d")} → ${format(parseISO(endDate), "MMM d, yyyy")}`;
  const pickupLocation = (() => {
    const loc = getEffectiveLocation(vehicle);
    return `${loc.city}, ${loc.state}`;
  })();

  // ─── Submit handler ───
  const handleSubmit = async () => {
    // Flip attemptedSubmit so the inline red error messages below each
    // required field become visible. Defer the scroll-to-first-error to
    // the next microtask so the error nodes are in the DOM by the time
    // we querySelector for them. No-ops on a clean submission because
    // querySelector returns null when no [data-booking-error] exists.
    setAttemptedSubmit(true);
    setTimeout(() => {
      document
        .querySelector("[data-booking-error]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);

    if (!agreedToTerms) {
      toast.error("Please agree to the rental terms");
      return;
    }
    if (!renterProfile) {
      toast.error("Please complete your renter profile first");
      return;
    }
    const eligibility = checkBookingEligibility(renterProfile, user?.email);
    if (!eligibility.eligible) {
      toast.error("Please complete verification before booking");
      return;
    }
    if (pickupType === "delivery" && !deliveryAddress.trim()) {
      toast.error("Please enter a delivery address");
      return;
    }
    if (!notes.trim()) {
      toast.error("Please include a message to the host about your trip");
      return;
    }

    setSubmitting(true);
    setStep(4);
    try {
      // Server-truth availability check. Reads ALL THREE sources of
      // unavailability — rental_bookings (active marketplace bookings),
      // availability_blocks (host manual blocks), and calendar_events
      // (imported iCal / Google Calendar blocks via calendar_sources).
      // A renter must not be able to check out against a vehicle that
      // has any overlapping row in any of the three tables. The prior
      // version queried the first two but missed calendar_events, so a
      // host who imported an Airbnb / Turo block via iCal could still
      // be double-booked on Vauren. See useBookingAvailability hook
      // for the shared client-side implementation.
      const [blocksRes, bookingsRes, eventsRes] = await Promise.all([
        supabase
          .from("availability_blocks")
          .select("start_date, end_date")
          .eq("vehicle_id", vehicle.id),
        supabase
          .from("rental_bookings")
          .select("start_date, end_date")
          .eq("vehicle_id", vehicle.id)
          .in("status", ["confirmed", "pending", "active"]),
        supabase
          .from("calendar_events")
          .select("start_date, end_date, status")
          .eq("vehicle_id", vehicle.id),
      ]);

      const requestedDays = eachDayOfInterval({
        start: parseISO(startDate),
        end: parseISO(endDate),
      }).map((d) => format(d, "yyyy-MM-dd"));

      const hasBlockConflict = (blocksRes.data || []).some((block) =>
        requestedDays.some((day) => day >= block.start_date && day <= block.end_date),
      );
      if (hasBlockConflict) {
        toast.error("This vehicle is unavailable for the selected dates.");
        setSubmitting(false);
        setStep(3);
        return;
      }

      const hasBookingConflict = (bookingsRes.data || []).some((booking) =>
        requestedDays.some(
          (day) => day >= booking.start_date && day <= booking.end_date,
        ),
      );
      if (hasBookingConflict) {
        toast.error(
          "This vehicle is already booked for some of the selected dates.",
        );
        setSubmitting(false);
        setStep(3);
        return;
      }

      const hasImportedConflict = (eventsRes.data || []).some((event) => {
        const status = (event.status || "").toLowerCase();
        if (status === "cancelled" || status === "declined") return false;
        if (!event.start_date || !event.end_date) return false;
        return requestedDays.some(
          (day) => day >= event.start_date && day <= event.end_date,
        );
      });
      if (hasImportedConflict) {
        toast.error(
          "This vehicle is already booked on another platform for some of the selected dates.",
        );
        setSubmitting(false);
        setStep(3);
        return;
      }

      // Combine date + HH:mm into timestamptz strings the edge function
      // stores directly on rental_bookings.pickup_time / dropoff_time.
      // Plain `${date}T${HH:mm}:00` is interpreted as local time and
      // converted to UTC on insert — good enough for booking windows.
      const pickupTimestamp = pickupTime ? `${startDate}T${pickupTime}:00` : null;
      const dropoffTimestamp = dropoffTime ? `${endDate}T${dropoffTime}:00` : null;

      // Persist form state to sessionStorage so a Stripe back-button or
      // cancel lands the renter back on this page with everything intact.
      // The cancel_url returned from Stripe deep-links to this same vehicle,
      // and the restore effect above picks the payload back up on mount.
      writePersistedState(id, {
        selectedAddons,
        selectedExtraIds,
        pickupType,
        deliveryAddress,
        notes,
        passengerCount,
        occasion,
        pickupTime,
        dropoffTime,
        step,
        startDate,
        endDate,
        savedAt: new Date().toISOString(),
      });

      const { data, error } = await supabase.functions.invoke("create-rental-checkout", {
        body: {
          vehicleId: vehicle.id,
          ownerId: vehicle.owner_id,
          startDate,
          endDate,
          totalDays,
          dailyRate: vehicle.daily_price,
          subtotal,
          discountPercent,
          discountAmount,
          cleaningFee,
          deliveryFee,
          platformFee,
          totalPrice,
          hostPayout,
          securityDeposit,
          pickupType,
          deliveryAddress: pickupType === "delivery" ? deliveryAddress : null,
          pickupLocation,
          pickupTime: pickupTimestamp,
          dropoffTime: dropoffTimestamp,
          notes: notes.trim(),
          cancellationPolicy: vehicle.cancellation_policy,
          bookingMode: vehicle.booking_mode,
          occasion,
          passenger_count: passengerCount,
          addons: selectedAddons,
          addon_total: addonTotal,
          // Step 2 extras (vehicle_extras rows). Sent both as raw IDs
          // (for the server-side trust check against vehicle_extras)
          // and as line-items the edge function can drop straight into
          // Stripe checkout.line_items. The edge function recomputes
          // amounts off the DB row so a tampered client price can't
          // bypass the host's real fees.
          selected_extra_ids: selectedExtraIds,
          selected_extras: extrasLineItems,
          extras_total: addonTotal,
        },
      });

      if (error) throw error;
      track.renter.bookingSubmitted({
        vehicleId: vehicle.id,
        totalDays,
        totalPrice,
        pickupType,
        bookingMode: vehicle.booking_mode,
      });

      if (data?.url) {
        track.renter.checkoutInitiated({ vehicleId: vehicle.id, totalPrice });
        window.location.href = data.url;
      } else if (data?.bookingId) {
        navigate(`/marketplace/rental-agreement?booking_id=${data.bookingId}`);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to create booking");
      setSubmitting(false);
      setStep(3);
    }
  };

  const ownerName = owner?.display_name || "your host";

  // ─── Step 4: branded processing screen ───
  if (step === 4) {
    return (
      <PageLayout>
        <div
          style={{
            minHeight: "100vh",
            background: C.page,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            padding: 24,
          }}
        >
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 32,
              fontWeight: 600,
              color: C.text,
              letterSpacing: "-0.02em",
            }}
          >
            VAUREN
          </div>
          <Loader2 size={36} color={C.bronze} className="animate-spin" />
          <div style={{ textAlign: "center", maxWidth: 360 }}>
            <p
              style={{
                fontFamily: SERIF,
                fontSize: 22,
                fontWeight: 600,
                color: C.text,
                margin: 0,
                marginBottom: 6,
                letterSpacing: "-0.005em",
              }}
            >
              Preparing your reservation
            </p>
            <p
              style={{
                fontFamily: SANS,
                fontSize: 14,
                color: C.textMuted,
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              We're confirming availability and securing your dates with {ownerName}. You'll be redirected to checkout in a moment.
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // ─── Step 1 left content: review ───
  const renderReview = () => (
    <>
      {/* Vehicle hero — sets the tone before the dry detail rows. */}
      <motion.div
        className="bv-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          position: "relative",
          width: "100%",
          height: 220,
          borderRadius: 16,
          overflow: "hidden",
          marginBottom: 24,
        }}
      >
        <img
          src={vehiclePhoto}
          alt={vehicle.title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "brightness(0.55)",
          }}
        />
        <div
          className="bv-hero-content"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            padding: "28px 32px",
          }}
        >
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            style={{
              fontFamily: SANS,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.7)",
              margin: "0 0 6px 0",
            }}
          >
            Your reservation
          </motion.p>
          <motion.h2
            className="bv-hero-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            style={{
              fontFamily: SERIF,
              fontSize: 28,
              fontWeight: 600,
              color: "#FFFFFF",
              margin: "0 0 8px 0",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {vehicle.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            style={{
              fontFamily: SANS,
              fontSize: 14,
              color: "rgba(255,255,255,0.85)",
              margin: 0,
            }}
          >
            {format(parseISO(startDate), "MMM d")} to {format(parseISO(endDate), "MMM d, yyyy")} · {totalDays} {totalDays === 1 ? "night" : "nights"}
          </motion.p>
        </div>
      </motion.div>

      <Card>
        <SectionHeading>Review your trip</SectionHeading>
      <p
        style={{
          fontFamily: SANS,
          fontSize: 15,
          color: C.text,
          lineHeight: 1.65,
          marginTop: 0,
          marginBottom: 24,
        }}
      >
        Confirm the details below before continuing to your reservation.
        You'll add pickup, protection, and a note to your host on the next
        step.
      </p>

      <div style={{ display: "grid", gap: 18 }}>
        <DetailRow
          label="Vehicle"
          value={vehicle.title}
        />
        <DetailRow
          label="Dates"
          value={`${dateRangeText} · ${totalDays} ${totalDays === 1 ? "night" : "nights"}`}
        />
        <DetailRow
          label="Pickup location"
          value={pickupLocation}
        />
        <DetailRow
          label="Host"
          value={ownerName}
        />
        <DetailRow
          label="Daily rate"
          value={`$${vehicle.daily_price.toLocaleString()} per night`}
        />
      </div>

      <Divider />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <TextLink onClick={() => navigate(`/marketplace/vehicle/${vehicle.id}`)}>
          <ChevronLeft size={14} /> Edit dates
        </TextLink>
        <div style={{ flex: 1, minWidth: 200 }}>
          <PrimaryButton onClick={() => setStep(skipExtras ? 3 : 2)}>
            {skipExtras ? "Continue to payment" : "Continue to add-ons"}
          </PrimaryButton>
        </div>
      </div>
    </Card>
    </>
  );

  // ─── Step 2 left content: extras pulled from vehicle_extras ───
  // SprinterRentals-style row: icon left, name+description middle,
  // price + Select/Delete right. Free items render $0.00 and are
  // pre-selected when extrasLoaded resolves. Each row's `selected`
  // state is driven by selectedExtraIds for parity with the running
  // total in the booking summary.
  const renderAddons = () => {
    const togglePaid = (extraId: string) =>
      setSelectedExtraIds((prev) =>
        prev.includes(extraId) ? prev.filter((x) => x !== extraId) : [...prev, extraId],
      );
    const selectedCount = selectedExtraRows.length;

    return (
      <Card>
        <SectionHeading>Extras</SectionHeading>
        <p
          style={{
            fontFamily: SANS,
            fontSize: 15,
            color: C.textMuted,
            lineHeight: 1.65,
            marginTop: 0,
            marginBottom: 20,
          }}
        >
          Optional add-ons offered by the host. Free items are added by default; tap Delete to remove.
        </p>

        {!extrasLoaded ? (
          <div
            style={{
              padding: "32px 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: C.taupe,
            }}
          >
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : extras.length === 0 ? (
          <div
            style={{
              padding: "24px 20px",
              background: C.page,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              fontFamily: SANS,
              fontSize: 14,
              color: C.textMuted,
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            This host hasn't added any extras for this listing yet.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {extras.map((e) => {
              const selected = selectedExtraIds.includes(e.id);
              const Icon = resolveExtraIcon(e.icon);
              const unitPrice = e.is_free ? 0 : Number(e.price) || 0;
              return (
                <div
                  key={e.id}
                  style={{
                    position: "relative",
                    width: "100%",
                    background: selected ? "#FFF8E8" : C.surface,
                    border: `${selected ? 2 : 1}px solid ${selected ? C.bronze : C.border}`,
                    borderRadius: 12,
                    padding: 16,
                    display: "flex",
                    gap: 14,
                    alignItems: "flex-start",
                    transition: "all 0.15s ease",
                    minWidth: 0,
                  }}
                >
                  {/* Icon — fixed 40px column per mobile spec. */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: selected ? "rgba(153,126,84,0.15)" : C.page,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: selected ? C.bronze : C.textMuted,
                    }}
                    aria-hidden
                  >
                    <Icon size={20} strokeWidth={1.6} />
                  </div>

                  {/* Name + description */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: SANS,
                        fontSize: 15,
                        fontWeight: 500,
                        color: C.text,
                        lineHeight: 1.35,
                      }}
                    >
                      {e.name}
                      {e.is_free && (
                        <span
                          style={{
                            display: "inline-block",
                            marginLeft: 8,
                            padding: "3px 8px",
                            borderRadius: 4,
                            background: C.bronze,
                            color: "#FFFFFF",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            verticalAlign: "middle",
                          }}
                        >
                          Free
                        </span>
                      )}
                    </div>
                    {e.description && (
                      <div
                        style={{
                          fontFamily: SANS,
                          fontSize: 13,
                          color: C.taupe,
                          marginTop: 4,
                          lineHeight: 1.5,
                        }}
                      >
                        {e.description}
                      </div>
                    )}
                  </div>

                  {/* Price + button */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 8,
                      flexShrink: 0,
                      minWidth: 96,
                    }}
                  >
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontFamily: SERIF,
                          fontSize: 18,
                          fontWeight: 600,
                          color: C.text,
                        }}
                      >
                        {formatMoney(unitPrice)}
                      </div>
                      <div
                        style={{
                          fontFamily: SANS,
                          fontSize: 11,
                          color: C.taupe,
                          marginTop: 2,
                        }}
                      >
                        {extraUnitLabel(e.price_type)}
                      </div>
                    </div>
                    {selected ? (
                      <button
                        type="button"
                        onClick={() => togglePaid(e.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          background: "transparent",
                          border: `1px solid ${C.text}`,
                          color: C.text,
                          fontFamily: SANS,
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "0 16px",
                          minHeight: 48,
                          borderRadius: 8,
                          cursor: "pointer",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => togglePaid(e.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          background: C.bronze,
                          border: "none",
                          color: "#FFFFFF",
                          fontFamily: SANS,
                          fontSize: 12,
                          fontWeight: 700,
                          padding: "0 18px",
                          minHeight: 48,
                          borderRadius: 8,
                          cursor: "pointer",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Plus size={14} /> Select
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Divider />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <TextLink onClick={() => setStep(1)}>
            <ChevronLeft size={14} /> Back to review
          </TextLink>
          <div style={{ flex: 1, minWidth: 200 }}>
            <PrimaryButton onClick={() => setStep(3)}>
              {selectedCount > 0
                ? `Continue with ${selectedCount} ${selectedCount === 1 ? "extra" : "extras"}`
                : "Continue without extras →"}
            </PrimaryButton>
          </div>
        </div>
      </Card>
    );
  };

  // ─── Step 3 left content: payment & details ───
  const renderPay = () => (
    <div style={{ display: "grid", gap: 24 }}>
      <Card>
        <SectionHeading>Pickup &amp; delivery</SectionHeading>
        <div style={{ display: "grid", gap: 12 }}>
          {/* Pickup radio only renders when pickup is actually an option.
              When the listing is delivery_available (delivery only),
              the renter doesn't get a choice. */}
          {deliveryOpt !== "delivery_available" && (
            <RadioOption
              selected={pickupType === "pickup"}
              onClick={() => setPickupType("pickup")}
              icon={MapPin}
              title="Pickup at host"
              subtitle={`Meet ${ownerName} at ${pickupLocation}.`}
            />
          )}
          {deliveryAvailable && (
            <RadioOption
              selected={pickupType === "delivery"}
              onClick={() => setPickupType("delivery")}
              icon={Truck}
              title={`Delivery${rawDeliveryFee > 0 ? ` · $${rawDeliveryFee.toLocaleString()}` : ""}`}
              subtitle={
                freeThreshold > 0
                  ? `Free on bookings of $${freeThreshold.toLocaleString()} or more.`
                  : deliveryOpt === "delivery_available"
                    ? "This van is delivered to your address."
                    : "Have the van delivered to your address."
              }
            />
          )}
        </div>
        {/* Cities served, if the host filled them in. Helps a renter
            understand whether they're inside the delivery zone before
            committing to an address. */}
        {deliveryAvailable && (vehicle as any).delivery_cities?.length > 0 && (
          <div
            style={{
              marginTop: 12,
              fontFamily: '"DM Sans", system-ui, sans-serif',
              fontSize: 13,
              color: "#4F413E",
              lineHeight: 1.6,
            }}
          >
            <strong>Serving:</strong>{" "}
            {((vehicle as any).delivery_cities as string[]).join(", ")}
          </div>
        )}
        {pickupType === "delivery" && (
          <div style={{ marginTop: 18 }}>
            <Label>Delivery address</Label>
            <AddressAutocomplete
              value={deliveryAddress}
              onChange={setDeliveryAddress}
              placeholder="Street, city, state"
            />
            {attemptedSubmit && !deliveryAddress.trim() && (
              <div
                data-booking-error
                style={{
                  marginTop: 8,
                  fontFamily: SANS,
                  fontSize: 13,
                  color: C.error,
                  lineHeight: 1.4,
                }}
              >
                Please enter a delivery address.
              </div>
            )}
            {(vehicle as any).delivery_notes && (
              <div
                style={{
                  marginTop: 8,
                  fontFamily: '"DM Sans", system-ui, sans-serif',
                  fontSize: 12,
                  color: "#9C9088",
                  lineHeight: 1.6,
                }}
              >
                {(vehicle as any).delivery_notes}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeading>Trip details</SectionHeading>
        <div style={{ display: "grid", gap: 18 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <Label>Pickup time</Label>
              <input
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontFamily: SANS,
                  fontSize: 15,
                  color: C.text,
                  minHeight: 44,
                }}
              />
              <div style={{ fontFamily: SANS, fontSize: 12, color: C.taupe, marginTop: 6 }}>
                Time of day you'll pick up on {format(parseISO(startDate), "MMM d")}.
              </div>
            </div>
            <div>
              <Label>Drop-off time</Label>
              <input
                type="time"
                value={dropoffTime}
                onChange={(e) => setDropoffTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontFamily: SANS,
                  fontSize: 15,
                  color: C.text,
                  minHeight: 44,
                }}
              />
              <div style={{ fontFamily: SANS, fontSize: 12, color: C.taupe, marginTop: 6 }}>
                Time you'll return on {format(parseISO(endDate), "MMM d")}.
              </div>
            </div>
          </div>
          <div>
            <Label>Passengers</Label>
            <PassengerCounter
              value={passengerCount}
              onChange={setPassengerCount}
              max={maxPassengers}
            />
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.taupe, marginTop: 6 }}>
              Up to {maxPassengers} passengers
            </div>
          </div>
          <div>
            <Label>Occasion</Label>
            <select
              className="bv-select"
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontFamily: SANS,
                fontSize: 15,
                color: C.text,
                cursor: "pointer",
                appearance: "none",
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%239C9088' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 16px center",
                paddingRight: 40,
                transition: "all 0.15s ease",
              }}
            >
              {OCCASION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>
              Message to your host{" "}
              <span style={{ color: C.error }}>*</span>
            </Label>
            <textarea
              className="bv-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tell your host about your trip. Share where you're going, who's travelling, and anything they should know."
              rows={4}
              style={{
                width: "100%",
                padding: "12px 16px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontFamily: SANS,
                fontSize: 15,
                color: C.text,
                resize: "vertical",
                lineHeight: 1.6,
                transition: "all 0.15s ease",
              }}
            />
            {attemptedSubmit && !notes.trim() && (
              <div
                data-booking-error
                style={{
                  marginTop: 8,
                  fontFamily: SANS,
                  fontSize: 13,
                  color: C.error,
                  lineHeight: 1.4,
                }}
              >
                Please include a message to the host about your trip.
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeading>Insurance</SectionHeading>
        <div
          style={{
            padding: 16,
            background: "#F8F4F0",
            borderRadius: 8,
            border: "1px solid #E0D8D2",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Shield size={18} color={C.bronze} style={{ flexShrink: 0, marginTop: 2 }} />
          <p
            style={{
              fontFamily: SANS,
              fontSize: 14,
              color: "#4F413E",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            The host's vehicle is covered under their commercial auto insurance
            policy. Roamly is Vauren's recommended insurance partner for hosts.
          </p>
        </div>
      </Card>

      <Card>
        <SectionHeading>Cancellation policy</SectionHeading>
        {(() => {
          // The same describePolicy helper that powers VehicleDetail.
          // Renter sees exactly what they're agreeing to here, before
          // hitting Book. Pickup is parsed from startDate + pickupTime
          // so the bullet copy is consistent with the booking the
          // renter is about to create.
          const pickupAt = startDate
            ? new Date(`${startDate}T${pickupTime || "10:00"}:00`)
            : null;
          const desc = describePolicy({
            now: new Date(),
            pickupAt,
            standardPolicy: vehicle.cancellation_policy,
            customPolicy: vehicle.custom_cancellation_policy,
          });
          return (
            <div
              style={{
                padding: 16,
                background: "#F8F4F0",
                borderRadius: 8,
                border: "1px solid #E0D8D2",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: C.bronze,
                  fontFamily: SANS,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {vehicle.custom_cancellation_policy ? "Custom policy" : "Standard policy"}
              </div>
              <div
                style={{
                  fontFamily: SERIF,
                  fontSize: 18,
                  fontWeight: 600,
                  color: C.text,
                  marginBottom: 10,
                }}
              >
                {desc.name}
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {desc.bullets.map((b, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: SANS,
                      fontSize: 14,
                      color: "#4F413E",
                      lineHeight: 1.6,
                      paddingLeft: 16,
                      position: "relative",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 9,
                        width: 6,
                        height: 1,
                        background: C.bronze,
                      }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  marginTop: 10,
                  fontFamily: SANS,
                  fontSize: 12,
                  color: "#9C9088",
                  lineHeight: 1.55,
                }}
              >
                Platform service fee (8%) is non-refundable. Security deposit
                holds are released automatically if you cancel.
              </div>
            </div>
          );
        })()}
      </Card>

      <Card>
        <SectionHeading>Identity &amp; eligibility</SectionHeading>
        <BookingEligibilityCheck renterProfile={renterProfile} userEmail={user?.email} />
      </Card>

      <Card>
        <label
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            cursor: "pointer",
            fontFamily: SANS,
            fontSize: 14,
            color: C.text,
            lineHeight: 1.6,
          }}
        >
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{
              marginTop: 3,
              width: 16,
              height: 16,
              accentColor: C.bronze,
              flexShrink: 0,
              cursor: "pointer",
            }}
          />
          <span>
            I agree to the{" "}
            <a
              href={`/marketplace/rental-agreement?vehicle_id=${vehicle.id}`}
              target="_blank"
              rel="noreferrer"
              className="bv-link"
              style={{
                color: C.text,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                transition: "color 0.2s ease",
              }}
            >
              rental agreement
            </a>{" "}
            and the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="bv-link"
              style={{
                color: C.text,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                transition: "color 0.2s ease",
              }}
            >
              Vauren terms
            </a>
            . I understand the security deposit will be authorised at pickup
            and released after a clean return.
          </span>
        </label>
        {attemptedSubmit && !agreedToTerms && (
          <div
            data-booking-error
            style={{
              marginTop: 12,
              fontFamily: SANS,
              fontSize: 13,
              color: C.error,
              lineHeight: 1.4,
            }}
          >
            Please agree to the rental agreement and terms to continue.
          </div>
        )}
      </Card>

      {/* Trust signals — three quick reassurances above the Reserve CTA. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 20,
          padding: "8px 0",
          marginBottom: 4,
        }}
      >
        {(() => {
          // Same-day bookings and trips whose 48h cancellation window has
          // already closed read "Non-refundable" — promising a future deadline
          // that's already passed (or impossible to hit) is misleading copy.
          const now = new Date();
          const startDateObj = parseISO(startDate);
          const cancellationDeadline = addDays(startDateObj, -2);
          const isToday = startDateObj.toDateString() === now.toDateString();
          const deadlineIsPast = cancellationDeadline < now;
          const cancellationLabel =
            isToday || deadlineIsPast
              ? "Non-refundable"
              : `Cancel before ${format(cancellationDeadline, "MMM d")}`;
          return [
            { icon: "🔒", text: "Secure payment" },
            { icon: "✓", text: cancellationLabel },
            { icon: "⚡", text: "Instant confirmation" },
          ];
        })().map((signal) => (
          <div
            key={signal.text}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <span style={{ fontSize: 12 }}>{signal.icon}</span>
            <span
              style={{
                fontFamily: SANS,
                fontSize: 12,
                color: C.taupe,
                whiteSpace: "nowrap",
              }}
            >
              {signal.text}
            </span>
          </div>
        ))}
      </motion.div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <TextLink onClick={() => setStep(skipExtras ? 1 : 2)}>
          <ChevronLeft size={14} /> {skipExtras ? "Back to review" : "Back to add-ons"}
        </TextLink>
        <div style={{ flex: 1, minWidth: 220 }}>
          <PrimaryButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Reserving..." : `Reserve · $${totalPrice.toLocaleString()}`}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout>
      <SEOHead
        title="Complete your booking | Vauren"
        description="Reserve your luxury Vauren van."
        canonical={`/marketplace/book/${vehicle.id}`}
      />
      <style>{RESPONSIVE_CSS}</style>

      <div style={{ minHeight: "100vh", background: C.page }}>
        <TopHeader
          vehicleTitle={vehicle.title}
          onBack={() => navigate(`/marketplace/vehicle/${vehicle.id}`)}
        />

        {cameFromCancelledCheckout && (
          <div
            role="status"
            style={{
              maxWidth: 1120,
              margin: "16px auto 0",
              padding: "12px 16px",
              background: "rgba(153,126,84,0.08)",
              border: `1px solid ${C.bronze}`,
              borderRadius: 10,
              color: C.text,
              fontFamily: SANS,
              fontSize: 13,
              lineHeight: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.bronze, flexShrink: 0 }} />
            Your booking was not completed. Your dates have been saved. Continue below.
          </div>
        )}

        <StepProgress
          current={step}
          steps={skipExtras ? STEPS.filter((s) => s.key !== 2) : STEPS}
        />

        <div className="bv-grid" style={{ paddingBottom: 80 }}>
          <main style={{ minWidth: 0 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              >
                {step === 1 && renderReview()}
                {step === 2 && renderAddons()}
                {step === 3 && renderPay()}
              </motion.div>
            </AnimatePresence>
          </main>

          <aside
            className="bv-summary"
            style={{ position: "sticky", top: 24, alignSelf: "start" }}
          >
            {/* Mobile-only collapse / expand bar. The matchMedia
                initialiser opens the summary on desktop and keeps it
                closed on mobile. CSS hides this button on >=960px. */}
            <button
              type="button"
              className="bv-summary-mobile-toggle"
              onClick={() => setSummaryOpen((v) => !v)}
              aria-expanded={summaryOpen}
              style={{
                width: "100%",
                minHeight: 48,
                padding: "12px 16px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                marginBottom: summaryOpen ? 12 : 0,
                color: C.text,
                fontFamily: SANS,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                boxShadow: "0 1px 3px rgba(44,36,32,0.04), 0 4px 16px rgba(44,36,32,0.06)",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <ChevronDown
                  size={16}
                  style={{
                    transition: "transform 0.2s ease",
                    transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
                {summaryOpen ? "Hide price details" : "View price details"}
              </span>
              <span style={{ fontFamily: SERIF, fontWeight: 600, color: C.text }}>
                ${(totalPrice + securityDeposit).toLocaleString()}
              </span>
            </button>
            <div style={{ display: summaryOpen ? "block" : "none" }}>
            <PriceSummary
              vehicleTitle={vehicle.title}
              vehiclePhoto={vehiclePhoto}
              pickupCity={pickupLocation}
              dateRangeText={dateRangeText}
              totalDays={totalDays}
              dailyPrice={vehicle.daily_price}
              subtotal={subtotal}
              discountPercent={discountPercent}
              discountAmount={discountAmount}
              cleaningFee={cleaningFee}
              deliveryFee={deliveryFee}
              platformFee={platformFee}
              totalPrice={totalPrice}
              securityDeposit={securityDeposit}
              extras={extrasLineItems.map((l) => ({
                id: l.id,
                name: l.name,
                total: l.total,
                is_free: l.is_free,
              }))}
            />
            </div>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div
      style={{
        fontFamily: SANS,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: C.taupe,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: SANS, fontSize: 15, color: C.text, fontWeight: 500 }}>
      {value}
    </div>
  </div>
);

interface RadioOptionProps {
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
  title: string;
  subtitle: string;
}

const RadioOption = ({ selected, onClick, icon: Icon, title, subtitle }: RadioOptionProps) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      display: "flex",
      gap: 14,
      alignItems: "flex-start",
      padding: 16,
      background: selected ? "rgba(153,126,84,0.06)" : C.surface,
      border: `1px solid ${selected ? C.bronze : C.border}`,
      borderRadius: 8,
      cursor: "pointer",
      textAlign: "left",
      transition: "all 0.15s ease",
      boxShadow: selected
        ? "0 0 0 2px rgba(153,126,84,0.18)"
        : "0 1px 2px rgba(44,36,32,0.03)",
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `2px solid ${selected ? C.bronze : C.border}`,
        background: C.surface,
        marginTop: 2,
        flexShrink: 0,
        position: "relative",
      }}
    >
      {selected && (
        <div
          style={{
            position: "absolute",
            inset: 3,
            borderRadius: "50%",
            background: C.bronze,
          }}
        />
      )}
    </div>
    <Icon size={20} color={selected ? C.bronze : C.textMuted} style={{ marginTop: 1, flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 2 }}>
        {title}
      </div>
      <div style={{ fontFamily: SANS, fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
        {subtitle}
      </div>
    </div>
  </button>
);

const PassengerCounter = ({
  value,
  onChange,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  max: number;
}) => {
  // Vauren vans seat 6–16. Show every integer from 6 up to the
  // vehicle's actual capacity (clamped at 16). Falling back to 6 keeps
  // the option list non-empty if the vehicle row has a stale capacity.
  const upper = Math.min(16, Math.max(6, max));
  const options = Array.from({ length: upper - 6 + 1 }, (_, i) => i + 6);
  const selected = options.includes(value) ? value : 6;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
      }}
    >
      <UsersIcon size={14} color={C.bronze} />
      <select
        value={selected}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Passengers"
        style={{
          fontFamily: SANS,
          fontSize: 15,
          fontWeight: 600,
          color: C.text,
          background: "transparent",
          border: "none",
          outline: "none",
          appearance: "none",
          cursor: "pointer",
          paddingRight: 4,
        }}
      >
        {options.map((n) => (
          <option key={n} value={n}>{n} passengers</option>
        ))}
      </select>
    </div>
  );
};

export default BookVehicle;
